import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Constants, fetchSdkConfig } from '../config/sdkConfig';
import * as meetingServices from '../services/meetingServices';
import userService from '../services/userServices';
// Create context
const SiteContext = createContext();

// Custom hook to use the context
export const useSiteContext = () => {
    const context = useContext(SiteContext);
    if (!context) {
        throw new Error('useSiteContext must be used within a SiteProvider');
    }
    return context;
};

// Provider component
export const SiteProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [meetingRole, setMeetingRole] = useState(null);
    const [isSafari, setIsSafari] = useState(false);
    const [meetingSettings, setMeetingSettings] = useState({
        guestCanUnmute: true,
    });
    const [showElementSection, setShowElementSection] = useState(false);
    const [folderElementList, setFolderElementList] = useState([]);
    const [activeElement, setActiveElement] = useState(null);
    const [webinarMode, setWebinarMode] = useState(null);
    const [meetingSession, setMeetingSession] = useState(null);
    const [selectedLayout, setSelectedLayout] = useState("gallery");
    const [hideSelfView, setHideSelfView] = useState(false);
    const [showNonVideoParticipants, setShowNonVideoParticipants] = useState(false);
    const [videoResolution, setVideoResolution] = useState('high');
    const [translationStatus, setTranslationStatus] = useState("stopped");
    const [localPinnedParticipant, setLocalPinnedParticipant] = useState([]);
    const [isMeetingReconnecting, setIsMeetingReconnecting] = useState(false);
    const [showPollResults, setShowPollResults] = useState(false);
    const [showPollsModal, setShowPollsModal] = useState(false);
    const [polls, setPolls] = useState([]);
    const [selectedPoll, setSelectedPoll] = useState(null);
    const [loadingPolls, setLoadingPolls] = useState(false);
    const [showPollResultsModal, setShowPollResultsModal] = useState(false);
    const [pollResults, setPollResultsInternal] = useState(null);
    const [customMeetingName, setCustomMeetingName] = useState("");

    // Wrapper to debug when pollResults changes
    const setPollResults = (value) => {
        console.log("SiteContext - setPollResults called with:", value);
        console.trace("setPollResults call stack");
        setPollResultsInternal(value);
    };
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [prompterElement, setPrompterElement] = useState(null);

    // SDK configuration state - loaded from backend
    const [sdkConfigLoaded, setSdkConfigLoaded] = useState(false);
    const [useAgora, setUseAgora] = useState(true); // Default to Agora

    // Co-hosts state - Set of participant IDs who have been promoted to co-host
    const [coHosts, setCoHosts] = useState(new Set());
    // Tracks participants explicitly demoted from co-host during this session.
    // This prevents metadata-based co-host detection from overriding a host's demotion.
    const [demotedCoHosts, setDemotedCoHosts] = useState(new Set());

    // Add a participant as co-host
    // Normalizes to string to handle Agora's numeric UIDs consistently
    // Returns same reference if ID already exists (prevents unnecessary re-renders)
    const addCoHost = useCallback((participantId) => {
        if (!participantId) return;
        const strId = String(participantId);
        // Remove from demoted list if re-promoting
        setDemotedCoHosts((prev) => {
            if (!prev.has(strId)) return prev;
            const newSet = new Set(prev);
            newSet.delete(strId);
            return newSet;
        });
        setCoHosts((prev) => {
            if (prev.has(strId)) return prev; // Already a co-host, no state change
            const newSet = new Set(prev);
            newSet.add(strId);
            return newSet;
        });
    }, []);

    // Remove a participant from co-host
    // Normalizes to string to handle Agora's numeric UIDs consistently
    // Returns same reference if ID doesn't exist (prevents unnecessary re-renders)
    const removeCoHost = useCallback((participantId) => {
        if (!participantId) return;
        const strId = String(participantId);
        // Mark as explicitly demoted so metadata won't override
        setDemotedCoHosts((prev) => {
            if (prev.has(strId)) return prev;
            const newSet = new Set(prev);
            newSet.add(strId);
            return newSet;
        });
        setCoHosts((prev) => {
            if (!prev.has(strId) && !prev.has(participantId)) return prev; // Not a co-host, no state change
            const newSet = new Set(prev);
            newSet.delete(strId);
            // Also try to delete the original type in case it was added differently
            newSet.delete(participantId);
            return newSet;
        });
    }, []);

    // Check if a participant is a co-host
    // Uses string comparison to handle Agora's numeric UIDs that may be stored/compared as different types
    // Also returns false if participant was explicitly demoted (even if metadata still says co-host)
    const isCoHost = useCallback((participantId) => {
        if (!participantId) return false;
        const strId = String(participantId);
        // Explicitly demoted — not a co-host regardless of metadata
        if (demotedCoHosts.has(strId)) return false;
        // Check the promoted Set
        if (coHosts.has(strId)) return true;
        if (coHosts.has(participantId)) return true;
        for (const coHostId of coHosts) {
            if (String(coHostId) === strId) return true;
        }
        return false;
    }, [coHosts, demotedCoHosts]);

    // Check if a participant was explicitly demoted from co-host
    const wasDemotedCoHost = useCallback((participantId) => {
        if (!participantId) return false;
        return demotedCoHosts.has(String(participantId));
    }, [demotedCoHosts]);

    // Clear all co-hosts (useful when meeting ends)
    const clearCoHosts = () => {
        setCoHosts(new Set());
        setDemotedCoHosts(new Set());
    };

    // Color mode / theme state
    const [colorMode, setColorMode] = useState(() => {
        try {
            const saved = localStorage.getItem('rlink_settings');
            if (saved) {
                const settings = JSON.parse(saved);
                return settings.colorMode || 'dark';
            }
        } catch (e) {}
        return 'dark';
    });

    // Apply color mode to document root
    useEffect(() => {
        const root = document.documentElement;

        // Remove existing theme classes
        root.classList.remove('theme-light', 'theme-dark', 'theme-auto');

        if (colorMode === 'auto') {
            // Use system preference
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            root.classList.add(prefersDark ? 'theme-dark' : 'theme-light');
            root.classList.add('theme-auto');

            // Listen for system preference changes
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = (e) => {
                root.classList.remove('theme-light', 'theme-dark');
                root.classList.add(e.matches ? 'theme-dark' : 'theme-light');
            };
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        } else {
            root.classList.add(`theme-${colorMode}`);
        }
    }, [colorMode]);

    // Listen for settings changes from SettingsModal
    useEffect(() => {
        const handleStorageChange = () => {
            try {
                const saved = localStorage.getItem('rlink_settings');
                if (saved) {
                    const settings = JSON.parse(saved);
                    if (settings.colorMode && settings.colorMode !== colorMode) {
                        setColorMode(settings.colorMode);
                    }
                }
            } catch (e) {}
        };

        // Check for changes periodically (for same-window updates)
        const interval = setInterval(handleStorageChange, 1000);

        // Listen for storage events from other tabs
        window.addEventListener('storage', handleStorageChange);

        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [colorMode]);

    // Load SDK configuration from backend on app startup
    useEffect(() => {
        const loadSdkConfig = async () => {
            try {
                const config = await fetchSdkConfig();
                setUseAgora(config.useAgora);
                setSdkConfigLoaded(true);
                console.log('[SiteContext] SDK config loaded: useAgora =', config.useAgora);
            } catch (error) {
                console.error('[SiteContext] Failed to load SDK config:', error);
                setSdkConfigLoaded(true); // Still mark as loaded to not block the app
            }
        };
        loadSdkConfig();
    }, []);

    // Load maintenance mode from localStorage
    useEffect(() => {
        const savedMaintenanceMode = localStorage.getItem('maintenanceMode');
        if (savedMaintenanceMode !== null) {
            setMaintenanceMode(savedMaintenanceMode === 'true');
        }
    }, []);

    // Save maintenance mode to localStorage when it changes
    const updateMaintenanceMode = (value) => {
        setMaintenanceMode(value);
        localStorage.setItem('maintenanceMode', value.toString());
    };

    useEffect(() => {
        const user = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        if (user && token) {
            setIsAuthenticated(true);
            setUser(JSON.parse(user));
            setToken(token);
        } else {
            setIsAuthenticated(false);
            setUser(null);
            setToken(null);
        }

        const ua = navigator.userAgent;
        const safariCheck = /^((?!chrome|android).)*safari/i.test(ua);
        setIsSafari(safariCheck);
    }, []);

    // Re-fetch user profile to keep plan/role in sync with DB
    useEffect(() => {
        if (!isAuthenticated) return;
        const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
        if (!storedUser) return;
        userService.getUserProfile().then((res) => {
            const profile = res?.data?.user || res?.data;
            console.log('[SiteContext] Profile API plan:', profile?.plan, 'localStorage plan:', storedUser?.plan);
            if (profile?.plan) {
                const updated = { ...storedUser, plan: profile.plan };
                console.log('[SiteContext] Updating user plan to:', profile.plan);
                localStorage.setItem('user', JSON.stringify(updated));
                setUser(prev => ({ ...prev, plan: profile.plan }));
            }
        }).catch((err) => { console.error('[SiteContext] Profile fetch failed:', err); });
    }, [isAuthenticated]);

    // Fetch white-label domain for authenticated users
    useEffect(() => {
        if (!isAuthenticated || !user) return;
        userService.getWhiteLabelSettings().then((res) => {
            const wl = res?.data;
            if (wl?.dnsVerified && wl?.subdomainPrefix && wl?.domain) {
                setUser((prev) => ({ ...prev, whitelabelDomain: `${wl.subdomainPrefix}.${wl.domain}` }));
            }
        }).catch(() => { /* ignore — user may not have whitelabel */ });
    }, [isAuthenticated]);

    // Reset customMeetingName when entering a new room
    const prevMeetingIdRef = useRef(null);
    useEffect(() => {
        const currentId = meetingSettings?.meetingId || meetingSettings?.roomId;
        if (currentId && prevMeetingIdRef.current && currentId !== prevMeetingIdRef.current) {
            setCustomMeetingName("");
        }
        prevMeetingIdRef.current = currentId;
    }, [meetingSettings?.meetingId, meetingSettings?.roomId]);

    useEffect(() => {
        if (customMeetingName && meetingSettings && meetingSession) {
            meetingServices.updateCustomMeetingName({
                meetingId: meetingSettings.meetingId,
                sessionId: meetingSession,
                customName: customMeetingName,
            });
        }
    }, [customMeetingName, meetingSettings, meetingSession]);

    useEffect(() => {
        if (!(meetingSettings)) return;
        console.log("meetingSettings", meetingSettings);
        console.log("meetingRole", meetingRole);
        let newMode;
        if (meetingSettings && meetingSettings.roomType == "Meeting") {
            newMode = Constants.modes.SEND_AND_RECV;
        } else if (meetingSettings && meetingSettings.roomType == "Webinar" && ["moderator", "co-host", "host", "presenter", "speaker"].includes(meetingRole)) {
            newMode = Constants.modes.SEND_AND_RECV;
        } else if (meetingSettings && meetingSettings.roomType == "Live Stream" && ["moderator", "co-host", "host", "speaker"].includes(meetingRole)) {
            newMode = Constants.modes.SEND_AND_RECV;
        } else {
            newMode = Constants.modes.RECV_ONLY;
        }
        // Only update if mode actually changes to prevent unnecessary re-render cascades
        // (e.g., co-host promotion in Meeting mode shouldn't cause any mode change)
        setWebinarMode(prev => prev === newMode ? prev : newMode);
    }, [meetingSettings, meetingRole]);

    const getMeetingSession = async (meetingId) => {
        try {
            const response = await meetingServices.getMeetingSession(meetingId);
            console.log("response from getMeetingSession", response, response.participants);
            setMeetingSession(response.data);
            setMeetingSettings((prev) => ({
                ...prev,
                guestCanUnmute: response?.sessionSettings?.guestCanMute ?? true,
                // Only overwrite hostApprovalRequired if sessionSettings actually exists;
                // otherwise preserve the value from the room config
                hostApprovalRequired: response?.sessionSettings
                    ? response.sessionSettings.waitingRoomEnabled
                    : prev.hostApprovalRequired,
            }));
        } catch (error) {
            console.log("Error in getMeetingSession", error);
        }
    }

    const handleVideoResolution = async (mMeeting, createCameraVideoTrack, resolution = 'high') => {
        console.log("handleVideoResolution", resolution, mMeeting, createCameraVideoTrack);
        const track = await createCameraVideoTrack({
            optimizationMode: "motion",
            encoderConfig: "h1080p_w1920p",
            facingMode: "environment",
            multiStream: false,
        });
        mMeeting.changeWebcam(track);
        setVideoResolution(resolution);
    }

    const value = {
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
        token,
        setToken,
        meetingRole,
        setMeetingRole,
        isSafari,
        setMeetingSettings,
        meetingSettings,
        showElementSection,
        setShowElementSection,
        folderElementList,
        setFolderElementList,
        activeElement,
        setActiveElement,
        webinarMode,
        setWebinarMode,
        getMeetingSession,
        meetingSession,
        setMeetingSession,
        selectedLayout,
        setSelectedLayout,
        hideSelfView,
        setHideSelfView,
        showNonVideoParticipants,
        setShowNonVideoParticipants,
        videoResolution,
        setVideoResolution,
        handleVideoResolution,
        translationStatus,
        setTranslationStatus,
        localPinnedParticipant,
        setLocalPinnedParticipant,
        isMeetingReconnecting,
        setIsMeetingReconnecting,
        showPollResults,
        setShowPollResults,
        showPollsModal,
        setShowPollsModal,
        polls,
        setPolls,
        selectedPoll,
        setSelectedPoll,
        loadingPolls,
        setLoadingPolls,
        showPollResultsModal,
        setShowPollResultsModal,
        pollResults,
        setPollResults,
        customMeetingName,
        setCustomMeetingName,
        maintenanceMode,
        setMaintenanceMode: updateMaintenanceMode,
        prompterElement,
        setPrompterElement,
        colorMode,
        setColorMode,
        // Co-host management
        coHosts,
        setCoHosts,
        addCoHost,
        removeCoHost,
        isCoHost,
        wasDemotedCoHost,
        clearCoHosts,
        // SDK configuration
        sdkConfigLoaded,
        useAgora,
    };

    return (
        <SiteContext.Provider value={value}>
            {children}
        </SiteContext.Provider>
    );
};

export default SiteContext;
